import { useEffect, useMemo, useRef } from 'react';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { RefetchSupportedType } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';

import type {
	useVersionHistoryContentVersionHistoryQuery as useVersionHistoryContentVersionHistoryQuery$data,
	useVersionHistoryContentVersionHistoryQueryVariables as useVersionHistoryContentVersionHistoryQuery$variables,
} from './__types__/useVersionHistoryContentVersionHistoryQuery.ts';
import { filterExpiredVersions } from './versionHistoryUtils.ts';
import type { VersionHistoryContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';

type useVersionHistoryType = {
	contentType: VersionHistoryContentType;
	contentId: string;
	onExpiredVersionsFiltered?: (params: { filteredCount: number; totalCount: number }) => void;
};

export const useVersionHistory = ({
	contentType,
	contentId,
	onExpiredVersionsFiltered,
}: useVersionHistoryType): {
	autosavedVersions: readonly {
		readonly author: {
			readonly displayName: string | null;
			readonly id: string | null;
			readonly isActive: boolean | null;
		} | null;
		readonly contentId: string;
		readonly contentProperties: {
			readonly latest: {
				readonly externalVersionId: string | null;
				readonly versionContainsAIContent: boolean | null;
			} | null;
		} | null;
		readonly number: number;
		readonly updatedTime: string;
	}[];
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	loadMoreVersions: () => void;
	cursor: string | null;
	hasNextPage: boolean;
	refetch: RefetchSupportedType<
		useVersionHistoryContentVersionHistoryQuery$data,
		useVersionHistoryContentVersionHistoryQuery$variables
	>;
	filteredCount: number;
	totalCount: number;
} => {
	const { data, loading, error, fetchMore, refetch } = useQueryInterop<
		useVersionHistoryContentVersionHistoryQuery$data,
		useVersionHistoryContentVersionHistoryQuery$variables
	>(
		graphql`
			query useVersionHistoryContentVersionHistoryQuery(
				$contentId: ID!
				$filter: ContentVersionHistoryFilter!
				$after: String
			) {
				contentVersionHistory(id: $contentId, after: $after, first: 30, filter: $filter) {
					nodes {
						author {
							id
							isActive
							displayName
						}
						contentId
						number
						updatedTime
						contentProperties {
							latest {
								externalVersionId
								versionContainsAIContent
							}
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
				}
			}
		`,
		{
			variables: { contentId, filter: { contentType } },
		},
	);
	const startCursor = data?.contentVersionHistory?.pageInfo?.startCursor || null;
	const rawHasNextPage = data?.contentVersionHistory?.pageInfo?.hasNextPage || false;

	const rawNodes = data?.contentVersionHistory?.nodes;

	const { autosavedVersions, hasFilteredOldVersions, filteredCount, totalCount } = useMemo(() => {
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (!fg('confluence_version_history_filter_expired_versions')) {
			return {
				autosavedVersions: rawNodes ?? [],
				hasFilteredOldVersions: false,
				filteredCount: 0,
				totalCount: rawNodes?.length ?? 0,
			};
		}

		return filterExpiredVersions(rawNodes);
	}, [rawNodes]);

	const hasNextPage = hasFilteredOldVersions ? false : rawHasNextPage;
	const cursor = hasFilteredOldVersions ? null : startCursor;

	const lastTrackedFilteredCountRef = useRef(0);
	useEffect(() => {
		if (!loading && filteredCount > 0 && filteredCount !== lastTrackedFilteredCountRef.current) {
			lastTrackedFilteredCountRef.current = filteredCount;
			onExpiredVersionsFiltered?.({ filteredCount, totalCount });
		}
	}, [filteredCount, totalCount, loading, onExpiredVersionsFiltered]);

	const loadMoreVersions = (): void => {
		if (hasNextPage && cursor && !loading) {
			void fetchMore({
				variables: { contentId, filter: { contentType }, after: cursor },
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.contentVersionHistory) {
						return prev;
					}

					const prevNodes = prev?.contentVersionHistory?.nodes || [];
					const fetchedNodes = fetchMoreResult.contentVersionHistory.nodes || [];
					const pageInfo =
						fetchMoreResult.contentVersionHistory.pageInfo || prev?.contentVersionHistory?.pageInfo;

					return {
						contentVersionHistory: {
							nodes: [...prevNodes, ...fetchedNodes],
							pageInfo,
							__typename: 'ContentVersionHistoryConnection',
						},
					};
				},
			});
		}
	};

	return {
		autosavedVersions,
		loading,
		error,
		loadMoreVersions,
		cursor,
		hasNextPage,
		refetch,
		filteredCount,
		totalCount,
	};
};
